import { useEffect } from "react";
import "./TextPromptModal.css";

interface ConfirmModalProps {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Render as destructive (red) confirm button. */
  destructive?: boolean;
  /** Single-button acknowledgement mode (e.g. a no-bypass block) — hides the cancel button. */
  hideCancel?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmModal({
  open,
  title,
  description,
  confirmLabel = "OK",
  cancelLabel = "Cancel",
  destructive = false,
  hideCancel = false,
  onConfirm,
  onCancel,
}: ConfirmModalProps) {
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCancel();
      if (e.key === "Enter") onConfirm();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel, onConfirm]);

  if (!open) return null;

  return (
    <div className="text-prompt-overlay" onClick={onCancel}>
      <div
        className="text-prompt-panel"
        role="alertdialog"
        aria-labelledby="confirm-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="confirm-modal-title" className="text-prompt-title">{title}</h2>
        {description && <p className="text-prompt-description">{description}</p>}
        <div className="text-prompt-actions">
          {!hideCancel && (
            <button type="button" className="text-prompt-btn" onClick={onCancel}>
              {cancelLabel}
            </button>
          )}
          <button
            type="button"
            className={`text-prompt-btn text-prompt-btn--primary${
              destructive ? " text-prompt-btn--danger" : ""
            }`}
            onClick={onConfirm}
            autoFocus
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
